import { modals } from '@mantine/modals';
import { useDisputeDebtor } from 'api';
import { DisputeReasonForm } from 'app/Private/Reasons/DisputeReasonForm/DisputeReasonForm';
import { PauseReasonForm } from 'app/Private/Reasons/PauseReasonForm/PauseReasonForm';
import { i18nKeys, useTranslation } from 'locales';
import { createElement } from 'react';
import { usePauseDebtor } from 'shared/hooks/use-pause-debtor';
import type { Debtor } from 'types/store/client-state';

type RecoveryDebtor = Pick<Debtor, 'id' | 'paused' | 'disputed'>;

type UseDebtorRecoveryActionsProps = {
  debtor: RecoveryDebtor;
  onSuccess: () => void;
};

type ReasonValues = { reason_id: string; end_at?: string };

type RecoveryToggle = {
  isActive: boolean;
  confirmBody: string;
  formTitle: string;
  reasonType: 'pause' | 'dispute';
  onLift: () => void;
  onSet: (values: ReasonValues) => unknown;
};

export const useDebtorRecoveryActions = ({ debtor, onSuccess }: UseDebtorRecoveryActionsProps) => {
  const { t } = useTranslation();
  const { pauseDebtor } = usePauseDebtor();
  const { mutate: disputeDebtor } = useDisputeDebtor();

  const isPaused = debtor.paused;
  const isDisputed = debtor.disputed;

  const handleSuccess = () => {
    modals.closeAll();
    onSuccess();
  };

  const openRecoveryToggle = ({
    isActive,
    confirmBody,
    formTitle,
    reasonType,
    onLift,
    onSet,
  }: RecoveryToggle) => {
    if (isActive) {
      modals.openConfirmModal({
        title: t(i18nKeys.CONFIRM),
        children: createElement('p', null, confirmBody),
        labels: { confirm: t(i18nKeys.CONFIRM), cancel: t(i18nKeys.CANCEL) },
        onConfirm: onLift,
      });
      return;
    }

    modals.open({
      title: formTitle,
      children:
        reasonType === 'pause'
          ? createElement(PauseReasonForm, { onSubmit: onSet })
          : createElement(DisputeReasonForm, { onSubmit: onSet }),
    });
  };

  const togglePause = () =>
    openRecoveryToggle({
      isActive: isPaused,
      confirmBody: t(i18nKeys.INVOICE.AYS_TO_UNPAUSE),
      formTitle: t(i18nKeys.CLIENT.PAUSE),
      reasonType: 'pause',
      onLift: () =>
        pauseDebtor({ id: debtor.id, data: { pause_debtor: false } }, { onSuccess: handleSuccess }),
      onSet: values =>
        new Promise<void>(resolve => {
          pauseDebtor(
            { id: debtor.id, data: { ...values, pause_debtor: true } },
            { onSuccess, onSettled: () => resolve() },
          );
        }),
    });

  const toggleDispute = () =>
    openRecoveryToggle({
      isActive: isDisputed,
      confirmBody: t(i18nKeys.AYS.CLIENT_ACTION_UNDISPUTED),
      formTitle: t(i18nKeys.MARK_AS_DISPUTED),
      reasonType: 'dispute',
      onLift: () =>
        disputeDebtor({ id: debtor.id, data: { dispute: false } }, { onSuccess: handleSuccess }),
      onSet: values =>
        new Promise<void>(resolve => {
          disputeDebtor(
            { id: debtor.id, data: { ...values, dispute: true } },
            { onSuccess, onSettled: () => resolve() },
          );
        }),
    });

  return { isPaused, isDisputed, togglePause, toggleDispute };
};
